// frontend/src/features/bapb/sections/CostSection.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * Cost Approach Engine (hearing-ready)
 * - Inputs from AIA / budget files (CSV/XLSX) with mapping and bucketing to Structure/Site/Soft/EP
 * - Time indexing via either CCI ratio (Now / Build) or monthly inflation % × months (compounded)
 * - Regional/location multiplier
 * - Soft costs & Entrepreneurial Profit (%, or absolute overrides)
 * - Depreciation engine: straight-line with effective age, economic life, condition modifier,
 *   optional functional/external (%, or absolute)
 * - Site improvements: per-line depreciation (year + life)
 * - Land value modes: manual (simple), room for extension later (from land comps)
 * - Summary card with full calculation trace & copy-to-clipboard
 * - Back-compat: still supports legacy flat fields (replacement_cost, soft_cost_pct, depreciation_pct, land_value)
 */

// ------------------------------ utils ------------------------------
const num = (v, d = 0) => {
  if (v === "" || v == null) return d;
  const s = String(v).replace(/[$,%\s,]/g, "");
  const n = Number(s);
  return Number.isFinite(n) ? n : d;
};
const toPctDec = (v) => {
  if (v == null || v === "") return 0;
  const s = String(v).trim().replace("%", "");
  const n = Number(s);
  return Number.isFinite(n) ? n / 100 : 0;
};
const fmt0 = (v) => (Number.isFinite(v) ? `$${Math.round(v).toLocaleString()}` : "—");
const fmt2 = (v) =>
  Number.isFinite(v) ? `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—";

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const parseDate = (s) => {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(+d) ? null : d;
};
const monthsBetween = (a, b) => {
  if (!a || !b) return 0;
  const ms = b.getTime() - a.getTime();
  return ms / (1000 * 60 * 60 * 24 * 30.44);
};
const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);

// ------------------------------ CSV/XLSX parsing ------------------------------
function splitCsvLine(line) {
  const out = [];
  let cur = "", inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
      else inQ = !inQ;
    } else if (ch === "," && !inQ) {
      out.push(cur); cur = "";
    } else cur += ch;
  }
  out.push(cur);
  return out.map((s) => s.trim());
}
function parseCsv(text) {
  const lines = String(text).replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n").filter((l) => l.trim() !== "");
  if (!lines.length) return { headers: [], rows: [] };
  const headers = splitCsvLine(lines[0] || "");
  const rows = lines.slice(1).map((ln) => splitCsvLine(ln));
  return { headers, rows };
}
async function parseXlsx(file) {
  if (!window.XLSX) throw new Error("XLSX parser not found. Make sure window.XLSX is available.");
  const buf = await file.arrayBuffer();
  const wb = window.XLSX.read(buf, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = window.XLSX.utils.sheet_to_json(ws, { header: 1 });
  const headers = (rows[0] || []).map((c) => String(c ?? ""));
  const out = rows.slice(1).map((r) => r.map((c) => (c == null ? "" : String(c))));
  return { headers, rows: out };
}

// ------------------------------ field mapping ------------------------------
const FIELD_DEFS = [
  { key: "desc",  label: "Description", synonyms: ["description","desc","item","scope","work","phase"] },
  { key: "bucket",label: "Bucket",      synonyms: ["bucket","category","cat","type","group"] }, // STRUCTURE / SITE / SOFT / EP
  { key: "cost",  label: "Cost ($)",    synonyms: ["amount","cost","value","total","extended","extension"] },
  { key: "date",  label: "Cost Date",   synonyms: ["date","as of","pricing date","phase date"] },
  { key: "year",  label: "Year (site)", synonyms: ["year","built year","completion year"] },
  { key: "life",  label: "Life (yrs)",  synonyms: ["life","useful life","economic life"] },
  { key: "notes", label: "Notes",       synonyms: ["notes","remark","comment"] },
];
const INDEX_BY_KEY = Object.fromEntries(FIELD_DEFS.map((f, i) => [f.key, i]));
const BUCKETS = ["STRUCTURE", "SITE", "SOFT", "EP"];

function autoMap(headers) {
  const map = {};
  headers.forEach((h, idx) => {
    const clean = String(h || "").toLowerCase().replace(/[^a-z0-9]/g, " ").replace(/\s+/g, " ").trim();
    let best = null, score = 0;
    for (const f of FIELD_DEFS) {
      for (const cand of [f.label, ...(f.synonyms || [])]) {
        const c = String(cand).toLowerCase();
        if (!c) continue;
        if (clean === c) { best = f.key; score = 100; break; }
        if (clean.includes(c) && score < 50) { best = f.key; score = 50; }
      }
      if (score === 100) break;
    }
    map[idx] = best || null;
  });
  return map;
}
function toFieldToIndex(headerIdxToField) {
  const f2i = {};
  for (const [colIdx, key] of Object.entries(headerIdxToField || {})) {
    if (key) f2i[key] = Number(colIdx);
  }
  return f2i;
}

// ------------------------------ small UI atoms ------------------------------
function Field({ label, value, onChange, placeholder, rightSlot, inputMode = "text" }) {
  return (
    <div>
      <label className="block text-xs text-slate-500 mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <input
          value={value ?? ""}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          inputMode={inputMode}
          className="w-full rounded-lg border border-slate-300 px-3 py-2"
        />
        {rightSlot}
      </div>
    </div>
  );
}
function Metric({ label, value, big = false, sublabel, noCurrency = false }) {
  const fmt = (v) =>
    Number.isFinite(v)
      ? Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })
      : "—";
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={big ? "text-xl font-semibold" : "text-base font-semibold"}>
        {noCurrency ? fmt(value) : `$${fmt(value)}`}
      </div>
      {sublabel && <div className="text-[11px] text-slate-500 mt-1">{sublabel}</div>}
    </div>
  );
}
function Checkbox({ checked, onChange, title }) {
  return (
    <input
      type="checkbox"
      className="h-4 w-4 rounded border-slate-300"
      checked={!!checked}
      onChange={(e) => onChange?.(e.target.checked)}
      title={title}
    />
  );
}
const uid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `r-${Math.random().toString(36).slice(2, 8)}`);

// ------------------------------ Mapping Dialog ------------------------------
function MappingDialog({ open, headers, sampleRows, initialFieldToIndex, onCancel, onImport }) {
  const [fieldToIndex, setFieldToIndex] = useState(initialFieldToIndex || {});
  if (!open) return null;
  const first3 = (sampleRows || []).slice(0, 3);

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30">
      <div className="w-[860px] max-w-[95vw] rounded-xl border border-slate-200 bg-white shadow-lg">
        <div className="px-4 py-3 border-b">
          <div className="font-semibold">Map Columns (AIA / Budget)</div>
          <div className="text-xs text-slate-500">
            Map to Description, Bucket, Cost, Date, Year, Life, Notes.
          </div>
        </div>
        <div className="p-4 space-y-3 max-h-[70vh] overflow-auto">
          {["desc", "bucket", "cost", "date", "year", "life", "notes"].map((key) => {
            const def = FIELD_DEFS[INDEX_BY_KEY[key]];
            const idx = fieldToIndex[key] ?? -1;
            return (
              <div key={key} className="grid grid-cols-5 gap-2 items-center">
                <div className="text-sm col-span-2">
                  <div className="font-medium">{def?.label}</div>
                </div>
                <div className="col-span-3">
                  <select
                    className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
                    value={idx}
                    onChange={(e) =>
                      setFieldToIndex((p) => ({ ...p, [key]: Number(e.target.value) }))
                    }
                  >
                    <option value={-1}>(not mapped)</option>
                    {headers.map((h, i) => (
                      <option key={i} value={i}>
                        {h || `(column ${i + 1})`}
                      </option>
                    ))}
                  </select>
                  {idx >= 0 && (
                    <div className="mt-1 text-[11px] text-slate-500 truncate">
                      {first3
                        .map((r) => String(r[idx] ?? ""))
                        .filter(Boolean)
                        .slice(0, 2)
                        .join(" · ") || "—"}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="px-4 py-3 border-t flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            className="rounded-lg px-3 py-1.5 border border-slate-300 bg-white"
          >
            Cancel
          </button>
          <button
            onClick={() => onImport(fieldToIndex)}
            className="rounded-lg px-3 py-1.5 border border-sky-300 bg-sky-50 text-sky-800"
          >
            Import
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------------------------ main ------------------------------
export default function CostSection({ data, onChange, onSave }) {
  // Back-compat with your previous minimal form (replacement_cost, soft_cost_pct, etc.)
  const initial = data?.cost || {};

  // --- Config / assumptions ---
  const [config, setConfig] = useState({
    valuationDate: initial.config?.valuationDate || todayISO(),
    buildCompletionDate: initial.config?.buildCompletionDate || "",
    regionalMultiplier: initial.config?.regionalMultiplier || "1.00",

    // Time indexing
    useCCIRatio: initial.config?.useCCIRatio ?? true,
    cciBuild: initial.config?.cciBuild || "",
    cciNow: initial.config?.cciNow || "",
    monthlyInflationPct: initial.config?.monthlyInflationPct || "",

    // Soft costs & entrepreneurial profit
    includeSoft: initial.config?.includeSoft ?? true,
    softPct: initial.config?.softPct || "",
    softAbs: initial.config?.softAbs || "",

    includeEP: initial.config?.includeEP ?? false,
    epPct: initial.config?.epPct || "",
    epAbs: initial.config?.epAbs || "",

    // Depreciation (building)
    economicLifeYears: initial.config?.economicLifeYears || "50",
    condition: initial.config?.condition || "Average", // Excellent / Average / Deferred
    functionalPct: initial.config?.functionalPct || "",
    functionalAbs: initial.config?.functionalAbs || "",
    externalPct: initial.config?.externalPct || "",
    externalAbs: initial.config?.externalAbs || "",
  });

  // --- Structure rows (RCN base) ---
  const seededStructure =
    Array.isArray(initial.structure) && initial.structure.length
      ? initial.structure
      : initial.replacement_cost
      ? [
          {
            id: uid(),
            desc: "Structure (lump sum)",
            bucket: "STRUCTURE",
            cost: initial.replacement_cost,
            date: initial.config?.buildCompletionDate || "",
          },
        ]
      : [];

  const [structure, setStructure] = useState(seededStructure);

  // --- Site improvements (each line depreciates) ---
  const [site, setSite] = useState(Array.isArray(initial.site) ? initial.site : []);

  // --- Land ---
  const [land, setLand] = useState({
    value: initial.land?.value ?? initial.land_value ?? "",
    notes: initial.land?.notes ?? "",
  });

  // --- Notes ---
  const [notes, setNotes] = useState(initial.notes || "");

  // --- Import (AIA/budget) ---
  const [mapUI, setMapUI] = useState({ open: false, headers: [], sample: [], guessed: {} });
  const [pendingRows, setPendingRows] = useState([]);

  // ------------------------------ import handlers ------------------------------
  async function handleFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    try {
      let parsed = { headers: [], rows: [] };
      if (ext === "xlsx") parsed = await parseXlsx(f);
      else parsed = parseCsv(await f.text());
      const guessed = autoMap(parsed.headers);
      setMapUI({
        open: true,
        headers: parsed.headers,
        sample: parsed.rows.slice(0, 10),
        guessed,
      });
      setPendingRows(parsed.rows);
    } catch (err) {
      alert(err?.message || "Failed to parse file.");
    } finally {
      e.target.value = "";
    }
  }
  function confirmImport(fieldToIndex) {
    const f2i = toFieldToIndex(fieldToIndex);
    const newStruct = [];
    const newSite = [];
    for (const row of pendingRows || []) {
      const get = (k) => {
        const i = f2i[k];
        return i == null || i < 0 ? "" : row[i] ?? "";
      };
      const bucket = String(get("bucket") || "").toUpperCase();
      const item = {
        id: uid(),
        desc: get("desc") || "",
        cost: get("cost") || "",
        date: get("date") || "",
        year: get("year") || "",
        life: get("life") || "",
        notes: get("notes") || "",
        bucket: BUCKETS.includes(bucket) ? bucket : "STRUCTURE",
      };
      if (item.bucket === "SITE") newSite.push(item);
      else if (item.bucket === "STRUCTURE") newStruct.push(item);
      else if (item.bucket === "SOFT") {
        const s = num(item.cost);
        if (s > 0)
          setConfig((p) => ({
            ...p,
            includeSoft: true,
            softAbs: String(num(p.softAbs) + s),
          }));
      } else if (item.bucket === "EP") {
        const ep = num(item.cost);
        if (ep > 0)
          setConfig((p) => ({ ...p, includeEP: true, epAbs: String(num(p.epAbs) + ep) }));
      }
    }
    setStructure((prev) => [...prev, ...newStruct]);
    setSite((prev) => [...prev, ...newSite]);
    setMapUI({ open: false, headers: [], sample: [], guessed: {} });
    setPendingRows([]);
  }

  // ------------------------------ calculations ------------------------------
  const valDate = useMemo(() => parseDate(config.valuationDate), [config.valuationDate]);
  const buildDate = useMemo(() => parseDate(config.buildCompletionDate), [config.buildCompletionDate]);
  const regionalMult = useMemo(() => {
    const m = num(config.regionalMultiplier || "1");
    return m > 0 ? m : 1;
  }, [config.regionalMultiplier]);

  function timeFactorForRow(rowDateStr) {
    const rowDate = parseDate(rowDateStr) || buildDate;
    if (!valDate || !rowDate) return 1;

    if (config.useCCIRatio) {
      const b = num(config.cciBuild);
      const n = num(config.cciNow);
      if (b > 0 && n > 0) return n / b;
      return 1;
    } else {
      const m = monthsBetween(rowDate, valDate);
      const r = toPctDec(config.monthlyInflationPct); // decimal per month
      if (r === 0 || m === 0) return 1;
      return Math.pow(1 + r, m); // compound monthly
    }
  }

  // Structure: time-index each row, then apply regional multiplier
  const structureCalcs = useMemo(() => {
    const lines = (structure || []).map((r) => {
      const base = num(r.cost);
      const tf = timeFactorForRow(r.date);
      const indexed = base * tf;
      const localized = indexed * regionalMult;
      return { id: r.id, base, tf, indexed, localized };
    });
    const sumBase = lines.reduce((a, b) => a + b.base, 0);
    const sumIndexed = lines.reduce((a, b) => a + b.indexed, 0);
    const RCN = lines.reduce((a, b) => a + b.localized, 0);
    return { lines, sumBase, sumIndexed, RCN };
  }, [
    structure,
    config.useCCIRatio,
    config.cciBuild,
    config.cciNow,
    config.monthlyInflationPct,
    regionalMult,
    valDate,
    buildDate,
  ]);

  // Soft & EP
  const softFromPct = config.includeSoft ? structureCalcs.RCN * toPctDec(config.softPct) : 0;
  const softAbs = config.includeSoft ? num(config.softAbs) : 0;
  const softTotal = softFromPct + softAbs;

  const epFromPct = config.includeEP
    ? (structureCalcs.RCN + softTotal) * toPctDec(config.epPct)
    : 0;
  const epAbs = config.includeEP ? num(config.epAbs) : 0;
  const epTotal = epFromPct + epAbs;

  const RCNAdj = structureCalcs.RCN + softTotal + epTotal;

  // Depreciation (building)
  const lifeYrs = Math.max(1, num(config.economicLifeYears || "50"));
  const effAgeYrs = useMemo(() => {
    if (!valDate || !buildDate) return 0;
    return Math.max(0, (valDate - buildDate) / (1000 * 60 * 60 * 24 * 365.25));
  }, [valDate, buildDate]);

  const conditionFactor = useMemo(() => {
    const c = String(config.condition || "Average").toLowerCase();
    if (c.includes("excellent") || c.includes("like")) return 0.85;
    if (c.includes("defer") || c.includes("poor")) return 1.15;
    return 1.0; // Average
  }, [config.condition]);

  const physicalPct = clamp01((effAgeYrs / lifeYrs) * conditionFactor);
  const physicalDep = RCNAdj * physicalPct;

  const functional = Math.max(num(config.functionalAbs), RCNAdj * toPctDec(config.functionalPct));
  const external = Math.max(num(config.externalAbs), RCNAdj * toPctDec(config.externalPct));

  const RCNLD_Building = Math.max(0, RCNAdj - physicalDep - functional - external);

  // Site improvements: depreciate each line item
  const siteCalcs = useMemo(() => {
    const lines = (site || []).map((r) => {
      const base = num(r.cost);
      const year = Number(String(r.year || "").replace(/[^\d]/g, ""));
      const life = Math.max(1, num(r.life || "20"));
      const age = valDate
        ? valDate.getFullYear() - (Number.isFinite(year) ? year : valDate.getFullYear())
        : 0;
      const pct = clamp01(age / life);
      const dep = base * pct;
      const remaining = Math.max(0, base - dep);
      return { id: r.id, base, pct, dep, remaining };
    });
    const totalRemaining = lines.reduce((a, b) => a + b.remaining, 0);
    const totalBase = lines.reduce((a, b) => a + b.base, 0);
    return { lines, totalBase, totalRemaining };
  }, [site, valDate]);

  // Land
  const landValue = num(land.value);

  // Final indicated value
  const indicatedValue = RCNLD_Building + siteCalcs.totalRemaining + landValue;

  // ------------------------------ push up (onChange) ------------------------------
  useEffect(() => {
    onChange?.({
      cost: {
        config,
        structure,
        site,
        land,
        notes,
        // Back-compat echo of flat fields (older reports may read these)
        replacement_cost: structureCalcs.RCN, // localized, time-indexed structure only
        soft_cost_pct: config.softPct,
        depreciation_pct: (physicalPct * 100).toFixed(2),
        land_value: land.value,
        // Calculations summary
        summary: {
          RCN_structure_base: structureCalcs.sumBase,
          RCN_structure_indexed: structureCalcs.sumIndexed,
          RCN_structure_localized: structureCalcs.RCN,
          soft_total: softTotal,
          ep_total: epTotal,
          RCN_adjusted: RCNAdj,
          physical_depr_amount: physicalDep,
          functional_depr_amount: functional,
          external_depr_amount: external,
          RCNLD_building: RCNLD_Building,
          site_remaining: siteCalcs.totalRemaining,
          land_value: landValue,
          indicated_value: indicatedValue,
          inputs: {
            effective_age_years: effAgeYrs,
            economic_life_years: lifeYrs,
            condition_factor: conditionFactor,
            regional_multiplier: regionalMult,
            time_method: config.useCCIRatio ? "CCI Ratio" : "Monthly Inflation",
            cci_build: config.cciBuild,
            cci_now: config.cciNow,
            monthly_inflation_pct: config.monthlyInflationPct,
          },
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    JSON.stringify(config),
    JSON.stringify(structure),
    JSON.stringify(site),
    JSON.stringify(land),
    notes,
    structureCalcs.RCN,
    structureCalcs.sumBase,
    structureCalcs.sumIndexed,
    softTotal,
    epTotal,
    RCNAdj,
    physicalDep,
    functional,
    external,
    RCNLD_Building,
    siteCalcs.totalRemaining,
    landValue,
    indicatedValue,
  ]);

  // ------------------------------ UI helpers ------------------------------
  function addStructure() {
    setStructure((p) => [
      ...p,
      { id: uid(), desc: "", bucket: "STRUCTURE", cost: "", date: config.buildCompletionDate || "", notes: "" },
    ]);
  }
  function removeStructure(id) {
    setStructure((p) => p.filter((r) => r.id !== id));
  }
  function setStructureRow(id, patch) {
    setStructure((p) => p.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }

  function addSite() {
    setSite((p) => [...p, { id: uid(), desc: "", bucket: "SITE", cost: "", year: "", life: "20", notes: "" }]);
  }
  function removeSite(id) {
    setSite((p) => p.filter((r) => r.id !== id));
  }
  function setSiteRow(id, patch) {
    setSite((p) => p.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }

  function copyTrace() {
    const t = [
      `STRUCTURE base ............ ${fmt0(structureCalcs.sumBase)}`,
      `Time indexed (sum) ........ ${fmt0(structureCalcs.sumIndexed)}`,
      `Regional x indexed ........ ${fmt0(structureCalcs.RCN)}`,
      config.includeSoft ? `+ Soft Costs ............... ${fmt0(softTotal)}` : null,
      config.includeEP ? `+ Entrepreneurial Profit ... ${fmt0(epTotal)}` : null,
      `= Adjusted RCN ............. ${fmt0(RCNAdj)}`,
      `- Physical Depreciation .... ${fmt0(physicalDep)} (${(physicalPct * 100).toFixed(2)}%)`,
      functional ? `- Functional Depreciation .. ${fmt0(functional)}` : null,
      external ? `- External Depreciation .... ${fmt0(external)}` : null,
      `= RCNLD (Building) ......... ${fmt0(RCNLD_Building)}`,
      siteCalcs.totalRemaining ? `+ Site Improvements (Dep.) . ${fmt0(siteCalcs.totalRemaining)}` : null,
      landValue ? `+ Land Value ............... ${fmt0(landValue)}` : null,
      `= Indicated Value (Cost) ... ${fmt0(indicatedValue)}`,
    ]
      .filter(Boolean)
      .join("\n");
    navigator.clipboard?.writeText(t);
  }

  // ------------------------------ render ------------------------------
  return (
    <div className="space-y-4">
      {/* Header actions */}
      <div className="flex flex-wrap items-center gap-2">
        <label className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm cursor-pointer hover:border-slate-400">
          <input
            type="file"
            accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={handleFile}
          />
          Import AIA / Budget (CSV/Excel)
        </label>
        <button
          onClick={copyTrace}
          className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm hover:border-slate-400"
          title="Copy calculation trace"
        >
          Copy Trace
        </button>
        <div className="ml-auto flex items-center gap-6 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <div className="text-xs text-slate-500">Indicated Value (Cost)</div>
          <div className="text-lg font-semibold">{fmt0(indicatedValue)}</div>
        </div>
      </div>

      {/* Mapping dialog */}
      <MappingDialog
        open={mapUI.open}
        headers={mapUI.headers}
        sampleRows={mapUI.sample}
        initialFieldToIndex={toFieldToIndex(mapUI.guessed)}
        onCancel={() => setMapUI({ open: false, headers: [], sample: [], guessed: {} })}
        onImport={confirmImport}
      />

      {/* Assumptions row */}
      <div className="rounded-xl border border-slate-200 bg-white p-3">
        {/* Top trio */}
        <div className="grid md:grid-cols-3 gap-3">
          <Field
            label="Valuation Date"
            value={config.valuationDate}
            onChange={(v) => setConfig((p) => ({ ...p, valuationDate: v }))}
          />
          <Field
            label="Build Completion Date"
            value={config.buildCompletionDate}
            onChange={(v) => setConfig((p) => ({ ...p, buildCompletionDate: v }))}
          />
          <Field
            label="Regional Multiplier"
            value={config.regionalMultiplier}
            onChange={(v) => setConfig((p) => ({ ...p, regionalMultiplier: v }))}
            placeholder="e.g., 1.05"
            inputMode="decimal"
          />
        </div>

        {/* Time Indexing + Soft/EP + Depreciation */}
        <div className="mt-3 grid md:grid-cols-3 gap-3">
          {/* Time Indexing */}
          <div className="rounded-lg border border-slate-200 p-3">
            <label className="text-xs text-slate-500">Time Indexing Method</label>
            <div className="mt-2 flex items-center gap-4">
              <label className="text-sm">
                <input
                  type="radio"
                  name="timeMode"
                  className="mr-1 align-middle"
                  checked={!!config.useCCIRatio}
                  onChange={() => setConfig((p) => ({ ...p, useCCIRatio: true }))}
                />
                CCI Ratio (Now / Build)
              </label>
              <label className="text-sm">
                <input
                  type="radio"
                  name="timeMode"
                  className="mr-1 align-middle"
                  checked={!config.useCCIRatio}
                  onChange={() => setConfig((p) => ({ ...p, useCCIRatio: false }))}
                />
                Monthly Inflation (%/mo)
              </label>
            </div>

            {config.useCCIRatio ? (
              <div className="mt-2 grid grid-cols-2 gap-2">
                <Field
                  label="CCI @ Build"
                  value={config.cciBuild}
                  onChange={(v) => setConfig((p) => ({ ...p, cciBuild: v }))}
                  inputMode="decimal"
                />
                <Field
                  label="CCI @ Now"
                  value={config.cciNow}
                  onChange={(v) => setConfig((p) => ({ ...p, cciNow: v }))}
                  inputMode="decimal"
                />
              </div>
            ) : (
              <div className="mt-2 grid grid-cols-2 gap-2">
                <Field
                  label="Monthly Inflation (%/mo)"
                  value={config.monthlyInflationPct}
                  onChange={(v) => setConfig((p) => ({ ...p, monthlyInflationPct: v }))}
                  inputMode="decimal"
                  placeholder="e.g., 0.5"
                />
                <Metric
                  label="Avg Months (Build→Val.)"
                  value={monthsBetween(buildDate, valDate)}
                  noCurrency
                />
              </div>
            )}
          </div>

          {/* Soft Costs & EP */}
          <div className="rounded-lg border border-slate-200 p-3">
            <label className="text-xs text-slate-500">Soft Costs & Entrepreneurial Profit</label>
            <div className="mt-2 space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={!!config.includeSoft}
                  onChange={(v) => setConfig((p) => ({ ...p, includeSoft: v }))}
                />
                Include Soft Costs
              </label>
              <div className="grid grid-cols-2 gap-2">
                <Field
                  label="Soft Costs (%)"
                  value={config.softPct}
                  onChange={(v) => setConfig((p) => ({ ...p, softPct: v }))}
                  inputMode="decimal"
                  placeholder="e.g., 7.5"
                />
                <Field
                  label="Soft Costs ($ add)"
                  value={config.softAbs}
                  onChange={(v) => setConfig((p) => ({ ...p, softAbs: v }))}
                  inputMode="decimal"
                />
              </div>

              <label className="flex items-center gap-2 text-sm mt-3">
                <Checkbox
                  checked={!!config.includeEP}
                  onChange={(v) => setConfig((p) => ({ ...p, includeEP: v }))}
                />
                Include Entrepreneurial Profit
              </label>
              <div className="grid grid-cols-2 gap-2">
                <Field
                  label="EP (%)"
                  value={config.epPct}
                  onChange={(v) => setConfig((p) => ({ ...p, epPct: v }))}
                  inputMode="decimal"
                  placeholder="e.g., 10"
                />
                <Field
                  label="EP ($ add)"
                  value={config.epAbs}
                  onChange={(v) => setConfig((p) => ({ ...p, epAbs: v }))}
                  inputMode="decimal"
                />
              </div>
            </div>
          </div>

          {/* Depreciation */}
          <div className="rounded-lg border border-slate-200 p-3">
            <label className="text-xs text-slate-500">Depreciation (Straight-line + modifiers)</label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <Field
                label="Economic Life (yrs)"
                value={config.economicLifeYears}
                onChange={(v) => setConfig((p) => ({ ...p, economicLifeYears: v }))}
                inputMode="numeric"
              />
              <div>
                <label className="block text-xs text-slate-500 mb-1">Condition</label>
                <select
                  className="w-full rounded-lg border border-slate-300 px-3 py-2"
                  value={config.condition}
                  onChange={(e) => setConfig((p) => ({ ...p, condition: e.target.value }))}
                >
                  <option>Excellent</option>
                  <option>Average</option>
                  <option>Deferred</option>
                </select>
              </div>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <Field
                label="Functional Depreciation (%)"
                value={config.functionalPct}
                onChange={(v) => setConfig((p) => ({ ...p, functionalPct: v }))}
                inputMode="decimal"
              />
              <Field
                label="Functional Depreciation ($)"
                value={config.functionalAbs}
                onChange={(v) => setConfig((p) => ({ ...p, functionalAbs: v }))}
                inputMode="decimal"
              />
              <Field
                label="External Depreciation (%)"
                value={config.externalPct}
                onChange={(v) => setConfig((p) => ({ ...p, externalPct: v }))}
                inputMode="decimal"
              />
              <Field
                label="External Depreciation ($)"
                value={config.externalAbs}
                onChange={(v) => setConfig((p) => ({ ...p, externalAbs: v }))}
                inputMode="decimal"
              />
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2">
              <Metric label="Adjusted RCN" value={RCNAdj} />
              <Metric
                label="Physical Depreciation"
                value={physicalDep}
                sublabel={`@ ${(physicalPct * 100).toFixed(2)}%`}
              />
              <Metric label="RCNLD (Building)" value={RCNLD_Building} />
            </div>
          </div>
        </div>
      </div>

      {/* Structure table */}
      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="px-3 py-2 border-b flex items-center">
          <div className="font-semibold">Structure (RCN base)</div>
          <div className="ml-auto flex items-center gap-6">
            <div className="text-xs text-slate-500">
              Base (sum): <span className="font-medium">{fmt0(structureCalcs.sumBase)}</span>
            </div>
            <div className="text-xs text-slate-500">
              Time-indexed: <span className="font-medium">{fmt0(structureCalcs.sumIndexed)}</span>
            </div>
            <div className="text-xs text-slate-500">
              RCN (localized): <span className="font-medium">{fmt0(structureCalcs.RCN)}</span>
            </div>
          </div>
        </div>
        <div className="overflow-auto">
          <table className="min-w-[1000px] w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-2 py-2 text-left w-[380px]">Description</th>
                <th className="px-2 py-2 text-left w-[120px]">Bucket</th>
                <th className="px-2 py-2 text-right w-[140px]">Cost ($)</th>
                <th className="px-2 py-2 text-right w-[140px]">Cost Date</th>
                <th className="px-2 py-2 text-right w-[120px]">Time Factor</th>
                <th className="px-2 py-2 text-right w-[160px]">Indexed ($)</th>
                <th className="px-2 py-2 text-right w-[160px]">Localized ($)</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {(structure || []).map((r) => {
                const base = num(r.cost);
                const tf = timeFactorForRow(r.date);
                const indexed = base * tf;
                const localized = indexed * regionalMult;
                return (
                  <tr key={r.id} className="border-t">
                    <td className="px-2 py-1">
                      <input
                        className="w-full rounded border border-slate-300 px-2 py-1"
                        value={r.desc || ""}
                        onChange={(e) => setStructureRow(r.id, { desc: e.target.value })}
                      />
                    </td>
                    <td className="px-2 py-1">
                      <select
                        className="w-full rounded border border-slate-300 px-2 py-1"
                        value={r.bucket || "STRUCTURE"}
                        onChange={(e) => setStructureRow(r.id, { bucket: e.target.value })}
                      >
                        {BUCKETS.map((b) => (
                          <option key={b} value={b}>
                            {b}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 py-1 text-right">
                      <input
                        className="w-full rounded border border-slate-300 px-2 py-1 text-right"
                        value={r.cost || ""}
                        onChange={(e) =>
                          setStructureRow(r.id, { cost: e.target.value.replace(/[^\d.-]/g, "") })
                        }
                        inputMode="decimal"
                      />
                    </td>
                    <td className="px-2 py-1 text-right">
                      <input
                        type="date"
                        className="rounded border border-slate-300 px-2 py-1"
                        value={r.date || ""}
                        onChange={(e) => setStructureRow(r.id, { date: e.target.value })}
                      />
                    </td>
                    <td className="px-2 py-1 text-right">{(tf || 1).toFixed(3)}</td>
                    <td className="px-2 py-1 text-right">{fmt2(indexed)}</td>
                    <td className="px-2 py-1 text-right font-medium">{fmt2(localized)}</td>
                    <td className="px-2 py-1">
                      <button
                        onClick={() => removeStructure(r.id)}
                        className="rounded-md border border-rose-300 bg-rose-50 px-2 py-1 text-rose-700 hover:border-rose-400"
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                );
              })}
              <tr className="border-t">
                <td colSpan={8} className="px-2 py-2">
                  <button
                    onClick={addStructure}
                    className="rounded-md px-3 py-1.5 border border-slate-300 bg-white hover:border-slate-400"
                  >
                    + Add Structure Line
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Site improvements */}
      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="px-3 py-2 border-b flex items-center">
          <div className="font-semibold">Site Improvements (depreciated individually)</div>
          <div className="ml-auto flex items-center gap-6">
            <div className="text-xs text-slate-500">
              Base (sum): <span className="font-medium">{fmt0(siteCalcs.totalBase)}</span>
            </div>
            <div className="text-xs text-slate-500">
              Remaining (dep.): <span className="font-medium">{fmt0(siteCalcs.totalRemaining)}</span>
            </div>
          </div>
        </div>
        <div className="overflow-auto">
          <table className="min-w-[1000px] w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-2 py-2 text-left w-[380px]">Description</th>
                <th className="px-2 py-2 text-right w-[140px]">Cost ($)</th>
                <th className="px-2 py-2 text-right w-[100px]">Year</th>
                <th className="px-2 py-2 text-right w-[120px]">Life (yrs)</th>
                <th className="px-2 py-2 text-right w-[120px]">Dep%</th>
                <th className="px-2 py-2 text-right w-[160px]">Remaining ($)</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {(site || []).map((r) => {
                const base = num(r.cost);
                const yr = Number(String(r.year || "").replace(/[^\d]/g, ""));
                const life = Math.max(1, num(r.life || "20"));
                const age = valDate
                  ? valDate.getFullYear() - (Number.isFinite(yr) ? yr : valDate.getFullYear())
                  : 0;
                const pct = clamp01(age / life);
                const remaining = Math.max(0, base * (1 - pct));
                return (
                  <tr key={r.id} className="border-t">
                    <td className="px-2 py-1">
                      <input
                        className="w-full rounded border border-slate-300 px-2 py-1"
                        value={r.desc || ""}
                        onChange={(e) => setSiteRow(r.id, { desc: e.target.value })}
                      />
                    </td>
                    <td className="px-2 py-1 text-right">
                      <input
                        className="w-full rounded border border-slate-300 px-2 py-1 text-right"
                        value={r.cost || ""}
                        onChange={(e) =>
                          setSiteRow(r.id, { cost: e.target.value.replace(/[^\d.-]/g, "") })
                        }
                        inputMode="decimal"
                      />
                    </td>
                    <td className="px-2 py-1 text-right">
                      <input
                        className="w-full rounded border border-slate-300 px-2 py-1 text-right"
                        value={r.year || ""}
                        onChange={(e) =>
                          setSiteRow(r.id, { year: e.target.value.replace(/[^\d]/g, "") })
                        }
                        inputMode="numeric"
                      />
                    </td>
                    <td className="px-2 py-1 text-right">
                      <input
                        className="w-full rounded border border-slate-300 px-2 py-1 text-right"
                        value={r.life || ""}
                        onChange={(e) =>
                          setSiteRow(r.id, { life: e.target.value.replace(/[^\d]/g, "") })
                        }
                        inputMode="numeric"
                      />
                    </td>
                    <td className="px-2 py-1 text-right">{(pct * 100).toFixed(1)}%</td>
                    <td className="px-2 py-1 text-right font-medium">{fmt2(remaining)}</td>
                    <td className="px-2 py-1">
                      <button
                        onClick={() => removeSite(r.id)}
                        className="rounded-md border border-rose-300 bg-rose-50 px-2 py-1 text-rose-700 hover:border-rose-400"
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                );
              })}
              <tr className="border-t">
                <td colSpan={7} className="px-2 py-2">
                  <button
                    onClick={addSite}
                    className="rounded-md px-3 py-1.5 border border-slate-300 bg-white hover:border-slate-400"
                  >
                    + Add Site Line
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Land & Notes */}
      <div className="rounded-xl border border-slate-200 bg-white p-3">
        <div className="grid md:grid-cols-3 gap-3">
          <Field
            label="Land Value"
            value={land.value}
            onChange={(v) => setLand((p) => ({ ...p, value: v }))}
            inputMode="decimal"
          />
          <Metric label="RCNLD (Building)" value={RCNLD_Building} />
          <Metric label="Site Improvements (Dep.)" value={siteCalcs.totalRemaining} />
        </div>
        <div className="mt-3 grid grid-cols-1">
          <label className="block text-xs text-slate-500 mb-1">
            Notes (sources, assumptions, exhibits)
          </label>
          <textarea
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2"
          />
        </div>
      </div>

      {/* Reconciliation / Actions row */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 flex flex-wrap items-center gap-3">
        <div className="text-sm">
          <span className="text-slate-500 mr-2">Indicated Value (Cost):</span>
          <span className="text-lg font-semibold">{fmt0(indicatedValue)}</span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => onSave?.()}
            className="rounded-lg px-3 py-2 border border-slate-300 bg-white hover:border-slate-400"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
